/** Work waiting for its plan's hourly quota, as the API describes it (never with dollar figures). */
export interface QuotaQueueInfo {
  code?: string;
  category?: string;
  category_label?: string;
  plan: string;
  plan_label: string;
  /** What is waiting, as a sentence subject: "Design", "This reply". */
  subject?: string;
  resets_at: string;
  upgrade_available: boolean;
  message?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/** The quota payload inside an API answer or error body (`{detail: {...}}` or the payload itself), if any. */
export const quotaQueueInfo = (value: unknown): QuotaQueueInfo | null => {
  const body = isRecord(value) && isRecord(value.detail) ? value.detail : value;

  if (!isRecord(body) || body.code !== 'quota_queued') return null;
  if (typeof body.plan_label !== 'string' || typeof body.resets_at !== 'string') return null;

  return body as unknown as QuotaQueueInfo;
};

export const quotaStartTime = (iso: string): string => (
  new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
);

/**
 * "Your edit can start again at 3:00 PM, when this hour's quota resets." Nothing sends queued work again by
 * itself: the user presses its action again. Once the reset time has passed, it says the work can start now.
 */
export const quotaWaitSentence = (notice: QuotaQueueInfo, now = Date.now()): string => {
  const subject = notice.subject || 'This work';

  if (new Date(notice.resets_at).getTime() <= now) return `${subject} can start again now: this hour's quota has reset.`;

  return `${subject} can start again at ${quotaStartTime(notice.resets_at)}, when this hour's quota resets.`;
};

/** What held work shows as its step (the backend's ``QUOTA_STEP_PREFIX``). */
export const isQuotaStep = (step?: string | null): boolean => Boolean(step?.startsWith('In the queue for '));

/** The press that starts each kind of held work again, by its notice's gate phase. */
const PRESS_BY_PHASE: Record<string, string> = {
  quota_queue: 'Continue',
  quota_queue_build: 'Resume build',
  quota_queue_notice: 'Run Updates',
};
const HOUR_MS = 3_600_000;

/** Quotas reset on the UTC clock hour, so a notice's wait ends at the first hour after it was posted. */
export const quotaResetMs = (postedMs: number): number => (
  postedMs ? (Math.floor(postedMs / HOUR_MS) + 1) * HOUR_MS : 0
);

/** "Quota resets in 14 min (3:30 AM), then press Resume build." Nothing starts the work by itself. */
export const quotaResetText = (resetMs: number, phase: string, now = Date.now()): string => {
  const press = PRESS_BY_PHASE[phase] || 'the button';
  const minutes = Math.ceil((resetMs - now) / 60_000);

  if (minutes <= 0) return `This hour's quota has reset. Press ${press} to start again.`;

  return `Quota resets in ${minutes} min (${quotaStartTime(new Date(resetMs).toISOString())}), then press ${press}.`;
};
